/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * Assertion validation (§15.10 "Validate the Assertions"). For each manifest the
 * validator checks that every claim-referenced assertion:
 *   A. hashes (§8.4.2.3) to the reference's stored `hash` (tamper detection),
 *   C. resolves within THIS manifest as a `self#jumbf` location,
 *   D. is well-formed CBOR / conforming JSON,
 * that the store carries no assertion the claim does not reference (B, insertion
 * attack), and that redactions are not abused (E). These are hard failures: a
 * mismatch/undeclared/missing assertion makes the manifest Invalid.
 *
 * Every check is a no-op for conformant, untampered manifests (verified against
 * all fixtures), so it does not change the state of clean inputs.
 */

import type { ParsedManifest } from '../model/parseManifestStore'
import { bytesEqual, hashJumbfBox, strictDigestName } from './hashJumbfBox'

/** An assertion-validation failure, carrying the code and the assertion label for the URL. */
export interface AssertionIssue {
    code: string
    /** Assertion label → URL suffix `c2pa.assertions/<label>`. */
    label: string
}

function lastSegment (url: string): string {
    const i = url.lastIndexOf('/')
    return i >= 0 ? url.slice(i + 1) : url
}

/**
 * The explicit manifest component of a JUMBF URI (`…/c2pa/<manifest>/…`), or null
 * when the URI is manifest-relative (`self#jumbf=c2pa.assertions/<label>` → the
 * current manifest).
 */
function manifestComponent (url: string): string | null {
    const m = url.match(/\/c2pa\/([^/]+)\//)
    return m ? m[1] : null
}

/**
 * Validates one manifest's assertions against its claim (§15.10). `buffer` is the
 * JUMBF manifest-store buffer every assertion `box.range` indexes (`ParsedStore.buffer`).
 */
export async function validateAssertions (m: ParsedManifest, buffer: Uint8Array): Promise<AssertionIssue[]> {
    const issues: AssertionIssue[] = []
    const claimAlg = m.claim.alg
    const redactedLabels = new Set(m.claim.redactedAssertions.map(lastSegment))

    // E. Redaction abuse. A claim redacting one of its OWN assertions →
    // assertion.selfRedacted; a redacted c2pa.actions(.v2) → assertion.action.redacted.
    for (const uri of m.claim.redactedAssertions) {
        const label = lastSegment(uri)
        if (label === 'c2pa.actions' || label === 'c2pa.actions.v2') {
            issues.push({ code: 'assertion.action.redacted', label })
        }
        const comp = manifestComponent(uri)
        const intoOwnManifest = comp === null || comp === m.label
        if (intoOwnManifest) {
            issues.push({ code: 'assertion.selfRedacted', label })
        }
    }

    // A / C / D — per claim-referenced assertion (created ∪ gathered / v1 assertions).
    const referencedLabels = new Set<string>()
    for (const ref of [...m.claim.createdAssertions, ...m.claim.gatheredAssertions]) {
        referencedLabels.add(ref.label)
        // A redacted reference is validated by the redaction rules above, not here.
        if (redactedLabels.has(ref.label)) continue

        // C. Must be a self#jumbf location resolving within THIS manifest.
        const comp = manifestComponent(ref.url)
        if (!ref.url.startsWith('self#jumbf=') || (comp !== null && comp !== m.label)) {
            issues.push({ code: 'assertion.outsideManifest', label: ref.label })
            continue
        }
        // §8.4 (l.1442): a reference whose label matches more than one assertion box
        // is ambiguous → treat as unresolved (PARSE-1). Fail closed rather than
        // hashing the first box (validate) while the reporter surfaces the last.
        if (m.duplicateAssertionLabels.has(ref.label)) {
            issues.push({ code: 'assertion.hashedURI.mismatch', label: ref.label })
            continue
        }
        const a = m.assertions.find(x => x.label === ref.label)
        if (!a) {
            issues.push({ code: 'assertion.missing', label: ref.label })
            continue
        }

        // D. Well-formedness (recorded at parse time).
        if (a.decodeError === 'cbor') { issues.push({ code: 'assertion.cbor.invalid', label: ref.label }); continue }
        if (a.decodeError === 'json') { issues.push({ code: 'assertion.json.invalid', label: ref.label }); continue }

        // §15.4 — hashed_uri alg, else the claim alg; unsupported/absent → reject.
        const algName = strictDigestName(ref.alg ?? claimAlg)
        if (!algName) { issues.push({ code: 'algorithm.unsupported', label: ref.label }); continue }

        // A. Recompute the assertion hash over its JUMBF superbox and compare.
        const computed = await hashJumbfBox(buffer, a.box, algName)
        if (!bytesEqual(computed, ref.hash)) {
            issues.push({ code: 'assertion.hashedURI.mismatch', label: ref.label })
        }
    }

    // B. Undeclared: any store assertion the claim does not reference is an insertion.
    for (const a of m.assertions) {
        if (!referencedLabels.has(a.label) && !redactedLabels.has(a.label)) {
            issues.push({ code: 'assertion.undeclared', label: a.label })
        }
    }

    return issues
}
